import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import ToastActions from '@store/toast/ToastActions';
import TriggerRuleActions from 'actions/triggers/TriggerRuleActions';
import TriggerRuleStore from 'stores/triggers/TriggerRuleStore';
import Breadcrumbs from 'components/pure/utils/Breadcrumbs';
import SmartAlertTypeConfig from './trigger-actions/SmartAlertTypeConfig';
import TriggerRuleAction, {
  TriggerActionType,
  TriggerEmailAction,
  TriggerFolderAction,
  TriggerSlackAction,
} from 'records/TriggerRuleAction';
import {browserHistory} from 'react-router';
import confirm from 'utils/confirm';
import noop from 'lodash/noop';
import SmartAlertLineItem from 'components/triggers/SmartAlertLineItem';
import BulletedRuleDescription from 'components/triggers/BulletedRuleDescription';
import Button from '@material-ui/core/Button';
import {TrendType} from 'records/TriggerTrend';
import UnsavedTriggerStore from 'stores/triggers/UnsavedTriggerStore';
import UnsavedTriggerActions from 'actions/triggers/UnsavedTriggerActions';
import './TriggerRulesStyles.styl';
import './SmartAlertEditDetails.styl';
import EditActionTypes from 'components/triggers/trigger-actions/EditActionTypes';
import Loading from 'components/pure/utils/Loading';
import {SendToSharedMe} from 'records/TriggerActionRecipient';
import {List, Map} from 'immutable';

@connectToStores
export default class SmartAlertEditDetails extends React.Component {
  constructor(props) {
    super(props);

    this.saveSmartAlert = this.saveSmartAlert.bind(this);
    this.cancelAction = this.cancelAction.bind(this);

    this.state = {ready: false};
  }

  static getStores() {
    return [TriggerRuleStore, UnsavedTriggerStore];
  }

  static getPropsFromStores(props) {
    if (!props) {
      return {};
    }
    const ruleId = props.params.ruleId;
    const unsavedTriggerStore = UnsavedTriggerStore.getState();
    const triggerStore = TriggerRuleStore.getState();

    return {
      unsavedTrend: unsavedTriggerStore.trend,
      actionType: unsavedTriggerStore.actionType,
      sendTo: unsavedTriggerStore.sendTo,
      damping: unsavedTriggerStore.damping,
      rule: triggerStore.rules.get(ruleId),
      antiRules: triggerStore.antiRules.get(ruleId),
    };
  }

  async componentDidMount() {
    const {location} = this.props;
    if (location && location.state) {
      const {state} = location;
      if (state.cancelNav) {
        this.setState({cancelNav: state.cancelNav});
      }
    }
    this.loadData().catch(console.error);
  }

  componentWillUnmount() {
    UnsavedTriggerActions.reset();
  }

  async loadData() {
    const ruleId = this.props.params.ruleId;
    const {rule} = this.props;
    this.setState({ready: false});

    if (!rule) {
      // wait for this one to come back because the store
      // is trying to insert the rule actions as a property
      // of the rule and it needs to be fetched for that
      // to be possible
      await TriggerRuleActions.fetchTriggerRule(ruleId);
    }

    await Promise.all([
      TriggerRuleActions.fetchAntiRules(ruleId),
      TriggerRuleActions.fetchTriggerTrends(ruleId),
      TriggerRuleActions.fetchTriggerRuleActions(ruleId),
    ]);

    this.setState({ready: true});
  }

  cancelAction() {
    const {ruleId} = this.props.params;

    const initialCreate =
      this.props.location && this.props.location.state && this.props.location.state.initialCreate;

    // set in CreateTriggerRule.js#saveTriggerClick - if we're creating
    // vs editing, delete and return to the list
    if (initialCreate) {
      confirm('Are you sure you want to cancel creating this Smart Alert?').then(async () => {
        await TriggerRuleActions.removeTriggerRule(ruleId);
        this.cancelNav(`/admin/actions`);
      }, noop);
    } else {
      this.cancelNav(`/admin/actions/${ruleId}`);
    }
  }

  cancelNav(path) {
    const {cancelNav} = this.state;
    if (cancelNav) {
      cancelNav();
    } else {
      browserHistory.push(path);
    }
  }

  async saveSmartAlert() {
    const ruleId = this.props.params.ruleId;
    const {damping, rule, unsavedTrend, sendTo, actionType} = this.props;

    const {id: trendId} = await saveTrend(rule, unsavedTrend, actionType);

    try {
      await Promise.all([
        TriggerRuleActions.updateTriggerRuleDamping(ruleId, damping),
        ...saveActions(trendId, rule, sendTo),
      ]);

      ToastActions.addToast({
        message: 'Smart Alert saved!',
      });
      browserHistory.push(`/admin/actions`);
    } catch (e) {
      console.error(e);
      ToastActions.addToast({
        type: 'error',
        message: 'Error saving Smart Alert notification settings.',
      });
    }
  }

  render() {
    const ruleId = this.props.params.ruleId;
    const {rule, antiRules, actionType, sendTo} = this.props;
    if (!this.state.ready) return <Loading />;

    if (!rule) return null;
    if (rule.rulePermissions.EDIT === undefined) return null;

    const actions = rule.actions.toList();
    const trend = rule.trends.first();

    const breadcrumbLinks = [
      {url: '/admin/actions', name: 'Smart Alerts'},
      {url: `/admin/actions/${ruleId}/`, name: 'Rule Detail'},
      {url: `/admin/actions/${ruleId}/edit`, name: 'Edit'},
    ];

    return (
      <div className="triggers-page smart-alert-edit-details">
        <Breadcrumbs links={breadcrumbLinks} />
        <div className="edit-container">
          <SmartAlertLineItem label="When this happens">
            <div className="trigger-description">
              <BulletedRuleDescription text={rule.name} />
            </div>
          </SmartAlertLineItem>
          <SmartAlertTypeConfig
            actionType={actionType}
            rule={rule}
            antiRules={antiRules}
            trend={trend}
          />
          {actionType && (
            <SmartAlertLineItem label="Do This">
              <EditActionTypes rule={rule} actions={actions} />
            </SmartAlertLineItem>
          )}
        </div>
        <div className="button-container">
          <Button
            variant="contained"
            color="primary"
            className="ev-button"
            disabled={
              // Has the user not selected a method of notifying?
              // (are all of the flags false? then disable)
              sendTo.every(x => !x)
            }
            onClick={this.saveSmartAlert}>
            Save
          </Button>
          <Button variant="outlined" className="ev-button" onClick={this.cancelAction}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }
}

async function saveTrend(rule, unsavedTrend, actionType) {
  const trend = rule.trends.first();
  const trendId = trend && trend.id;
  // Dont save non-trend
  if (actionType !== 'trend') return {id: trendId};

  // If the trend hasn't changed at all dont bother saving.
  if (unsavedTrend.equals(trend)) return {id: trendId};

  const trendType = unsavedTrend.trendType;
  try {
    const updatedTrend = unsavedTrend
      .update('subPeriod', subPeriod =>
        trendType === TrendType.AVERAGE_OF_TIMES ? subPeriod : null
      )
      .set('ruleId', rule.id);

    if (updatedTrend.id) {
      return await TriggerRuleActions.updateTriggerTrend(rule.id, updatedTrend);
    } else {
      return await TriggerRuleActions.createTriggerTrend(rule.id, updatedTrend);
    }
  } catch (e) {
    console.error(e);
    ToastActions.addToast({
      type: 'error',
      message: 'Error saving Smart Alert Trend.',
    });
  }
}

function findId(rule, type) {
  const action = rule.actions.find(a => a.configuration.type === type);
  return action && action.id;
}

const types = [TriggerActionType.FOLDER, TriggerActionType.EMAIL, TriggerActionType.SLACK];
const recipients = List([new SendToSharedMe()]);
const config = Map({
  [TriggerActionType.FOLDER]: new TriggerFolderAction({
    notificationTo: recipients,
  }),
  [TriggerActionType.EMAIL]: new TriggerEmailAction({emailTo: recipients}),
  [TriggerActionType.SLACK]: new TriggerSlackAction({notificationTo: recipients}),
});

function saveActions(trendId, rule, sendTo) {
  // Create an action for each of the notification types
  const actions = types.map(actionType => {
    return new TriggerRuleAction({
      id: findId(rule, actionType),
      trendId,
      ruleId: rule.id,
      active: sendTo.get(actionType),
      configuration: config.get(actionType),
      type: TriggerActionType.getEntityType(actionType),
    });
  });

  const triggerRuleActions = actions
    .filter(action => {
      // If the action has an id (has been saved before) update it regardless of active
      if (action.id) return true;

      // If our action is new then only send it if the action is active.
      return action.active;
    })
    .map(action => TriggerRuleActions.upsertTriggerRuleAction(rule.id, action));

  return triggerRuleActions;
}
